import {
  AlertModal,
  Box,
  DestructiveModal,
  Text,
  Toast,
  useOpenModal,
} from '@modusign/design-system';

import useDeleteDrafts from '@app/features/form/hooks/useDeleteDrafts';
import useOpenErrorModal from '@app/hooks/useOpenErrorModal';
import { useWorkspaceId } from '@app/hooks/useWorkspaceId';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  draftIds: string[];
}

export default function DeleteDraftsModal({ isOpen, onConfirm, onClose, draftIds }: Props) {
  const openModal = useOpenModal();
  const { createErrorHandler } = useOpenErrorModal();

  const workspaceId = useWorkspaceId();
  const deleteDrafts = useDeleteDrafts();

  return (
    <DestructiveModal
      confirmButtonLabel="삭제하기"
      isLoading={deleteDrafts.isLoading}
      isOpen={isOpen}
      maxWidth="xxs"
      title="서식 삭제"
      variant="destructive"
      onClose={onClose}
      onConfirm={() =>
        deleteDrafts.mutate([workspaceId, { draftIds }], {
          onSuccess: ({ error, results }) => {
            if (!error) {
              Toast.success({ description: `${results.length}개의 서식을 삭제했습니다.` });
            }
            if (error) {
              const successfulResults = results.filter((r) => !r.error);
              if (successfulResults.length) {
                openModal(
                  (modalProps) => (
                    <AlertModal
                      {...modalProps}
                      maxWidth="xxs"
                      title="서식 삭제 완료"
                      variant="completed"
                    >
                      {`${successfulResults.length}개의 서식을 삭제했습니다.`}
                      <Box
                        bg="boxBackgroundNeutralBase"
                        borderColor="boxBorderNeutralBase"
                        borderStyle="solid"
                        borderWidth="1px"
                        mt={6}
                        px={6}
                        py={7}
                      >
                        {'이미 삭제되었거나, 접근 권한이 없는 서식은 삭제되지 않습니다.'}
                      </Box>
                    </AlertModal>
                  ),
                  { keepOnUnmount: true }
                );
              } else {
                openModal(
                  (modalProps) => (
                    <AlertModal
                      {...modalProps}
                      maxWidth="xxs"
                      title="서식 삭제 안내"
                      variant="warning"
                    >
                      {'삭제할 수 있는 서식이 없습니다.'}
                      <Box
                        bg="boxBackgroundNeutralBase"
                        borderColor="boxBorderNeutralBase"
                        borderStyle="solid"
                        borderWidth="1px"
                        mt={6}
                        px={6}
                        py={7}
                      >
                        {'이미 삭제되었거나, 접근 권한이 없는 서식은 삭제되지 않습니다.'}
                      </Box>
                    </AlertModal>
                  ),
                  { keepOnUnmount: true }
                );
              }
            }
            onConfirm();
            onClose();
          },
          onError: createErrorHandler({ title: '서식 삭제 실패' }),
        })
      }
    >
      <Text textAlign="center">{`선택한 ${draftIds.length}개의 서식을 삭제합니다.`}</Text>
      <Text textAlign="center">삭제된 서식은 복구할 수 없습니다.</Text>
    </DestructiveModal>
  );
}
